import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import ChannelBadge from '../ChannelBadge';
import CopyablePhone from '../CopyablePhone';
import {
    Plus,
    Trash2,
    RefreshCw,
    ChevronDown,
    ChevronRight,
    Users,
    Hash,
    AlertCircle,
    Loader2,
    Check,
    X
} from 'lucide-react';

/* ─── tiny helpers ─── */
const API = (path, opts, authFetch) => authFetch(`/api/admin${path}`, opts);
const getChannelLabel = (channel) => {
    const customName = typeof channel.custom_name === 'object'
        ? channel.custom_name?.name
        : channel.custom_name;

    return customName || channel.display_phone_number || channel.id;
};

/* ─── Role card ─── */
function RoleCard({ role, allChannels, allAgents, authFetch, onDeleted, onUpdated }) {
    const [open, setOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [busy, setBusy] = useState(false);

    const roleChannelIds = new Set((role.channels || []).map(c => c.id));
    const roleAgentIds = new Set((role.agents || []).map(a => a.id));
    const availableChannels = allChannels.filter(c => !roleChannelIds.has(c.id));
    const availableAgents = allAgents.filter(a => !roleAgentIds.has(a.id));

    const addChannel = async (channelId) => {
        setBusy(true);
        await API(`/roles/${role.id}/channels`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ channel_id: channelId })
        }, authFetch);
        onUpdated();
        setBusy(false);
    };

    const removeChannel = async (channelId) => {
        setBusy(true);
        await API(`/roles/${role.id}/channels/${channelId}`, { method: 'DELETE' }, authFetch);
        onUpdated();
        setBusy(false);
    };

    const addAgent = async (agentId) => {
        setBusy(true);
        await API(`/roles/${role.id}/agents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ agent_id: agentId })
        }, authFetch);
        onUpdated();
        setBusy(false);
    };

    const removeAgent = async (agentId) => {
        setBusy(true);
        await API(`/roles/${role.id}/agents/${agentId}`, { method: 'DELETE' }, authFetch);
        onUpdated();
        setBusy(false);
    };

    const deleteRole = async () => {
        if (!window.confirm(`¿Eliminar el rol "${role.name}"? Los agentes perderán este rol.`)) return;
        setDeleting(true);
        await API(`/roles/${role.id}`, { method: 'DELETE' }, authFetch);
        onDeleted(role.id);
    };

    return (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3">
                <button
                    onClick={() => setOpen(v => !v)}
                    className="flex-1 flex items-center gap-3 text-left"
                >
                    {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    <div>
                        <p className="font-semibold text-slate-100">{role.name}</p>
                        {role.description && <p className="text-xs text-slate-500">{role.description}</p>}
                    </div>
                    <div className="flex items-center gap-3 ml-3">
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Hash size={12} /> {(role.channels || []).length} canales
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Users size={12} /> {(role.agents || []).length} agentes
                        </span>
                    </div>
                </button>
                <button
                    onClick={deleteRole}
                    disabled={deleting}
                    className="p-1.5 text-red-500/70 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                    {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
            </div>

            {/* Expanded body */}
            {open && (
                <div className="border-t border-slate-800 px-4 py-4 space-y-5">
                    {/* Channels */}
                    <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Canales</p>
                        <div className="space-y-1.5 mb-3">
                            {(role.channels || []).length === 0 && (
                                <p className="text-xs text-slate-600 italic">Sin canales asignados</p>
                            )}
                            {(role.channels || []).map(ch => (
                                <div key={ch.id} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-1.5">
                                    <ChannelBadge 
                                        color={ch.color} 
                                        channel_name={ch.display_phone_number} 
                                        custom_name={ch.custom_name}
                                        size="sm"
                                        showPhoneActions={false}
                                    />
                                    {ch.display_phone_number ? (
                                        <CopyablePhone phone={ch.display_phone_number} className="flex-1" textClassName="text-sm text-slate-200">
                                            {getChannelLabel(ch)}
                                        </CopyablePhone>
                                    ) : (
                                        <span className="flex-1 text-sm text-slate-200">{getChannelLabel(ch)}</span>
                                    )}
                                    <button
                                        onClick={() => removeChannel(ch.id)}
                                        disabled={busy}
                                        className="text-slate-500 hover:text-red-400 transition-colors"
                                    >
                                        <X size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {availableChannels.length > 0 && (
                            <select
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-primary/60"
                                value=""
                                onChange={e => e.target.value && addChannel(e.target.value)}
                                disabled={busy}
                            >
                                <option value="">+ Añadir canal…</option>
                                {availableChannels.map(c => (
                                    <option key={c.id} value={c.id}>{c.name || c.hub_channel_id}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Agents */}
                    <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Agentes</p>
                        <div className="space-y-1.5 mb-3">
                            {(role.agents || []).length === 0 && (
                                <p className="text-xs text-slate-600 italic">Sin agentes asignados</p>
                            )}
                            {(role.agents || []).map(ag => (
                                <div key={ag.id} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-1.5">
                                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                                        {(ag.full_name || ag.email || '?')[0].toUpperCase()}
                                    </div>
                                    <span className="flex-1 text-sm text-slate-200">{ag.full_name || ag.email}</span>
                                    <button
                                        onClick={() => removeAgent(ag.id)}
                                        disabled={busy}
                                        className="text-slate-500 hover:text-red-400 transition-colors"
                                    >
                                        <X size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {availableAgents.length > 0 && (
                            <select
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-primary/60"
                                value=""
                                onChange={e => e.target.value && addAgent(e.target.value)}
                                disabled={busy}
                            >
                                <option value="">+ Añadir agente…</option>
                                {availableAgents.map(a => (
                                    <option key={a.id} value={a.id}>{a.full_name || a.email}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Unassigned conversations section ─── */
function UnassignedConversations({ allChannels, authFetch }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assignments, setAssignments] = useState({});
    const [saving, setSaving] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API('/channels/unassigned?limit=50', {}, authFetch);
            const data = await res.json();
            setItems(data.conversations || []);
        } catch (_) { /* ignore */ }
        setLoading(false);
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);

    const assign = async (convId) => {
        const channelId = assignments[convId];
        if (!channelId) return;
        setSaving(s => ({ ...s, [convId]: true }));
        await API(`/channels/${channelId}/conversations/${convId}`, { method: 'PATCH' }, authFetch);
        setItems(prev => prev.filter(c => c.conversation_id !== convId));
        setSaving(s => { const n = { ...s }; delete n[convId]; return n; });
    };

    if (loading) return <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Cargando…</div>;
    if (items.length === 0) return <p className="text-sm text-slate-500 italic">No hay conversaciones sin canal asignado.</p>;

    return (
        <div className="space-y-2">
            {items.map(conv => (
                <div key={conv.conversation_id} className="flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                        {conv.contact_phone ? (
                            <CopyablePhone phone={conv.contact_phone} textClassName="text-sm text-slate-200" />
                        ) : (
                            <p className="text-sm text-slate-200 truncate">{conv.conversation_id}</p>
                        )}
                        <p className="text-xs text-slate-500">{conv.contact_name || 'Sin nombre'}</p>
                    </div>
                    <select
                        className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-200 focus:outline-none focus:border-primary/60"
                        value={assignments[conv.conversation_id] || ''}
                        onChange={e => setAssignments(a => ({ ...a, [conv.conversation_id]: e.target.value }))}
                    >
                        <option value="">Canal…</option>
                        {allChannels.map(c => (
                            <option key={c.id} value={c.id}>{c.name || c.hub_channel_id}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => assign(conv.conversation_id)}
                        disabled={!assignments[conv.conversation_id] || saving[conv.conversation_id]}
                        className="p-1.5 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-colors disabled:opacity-40"
                    >
                        {saving[conv.conversation_id] ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    </button>
                </div>
            ))}
        </div>
    );
}

/* ─── Main page ─── */
export default function Roles() {
    const { authFetch } = useAuth();
    const [roles, setRoles] = useState([]);
    const [allChannels, setAllChannels] = useState([]);
    const [allAgents, setAllAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState(null);
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleDesc, setNewRoleDesc] = useState('');
    const [creating, setCreating] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [rolesRes, channelsRes, agentsRes] = await Promise.all([
                API('/roles', {}, authFetch),
                API('/channels', {}, authFetch),
                API('/agents', {}, authFetch),
            ]);
            const [rolesData, channelsData, agentsData] = await Promise.all([
                rolesRes.json(),
                channelsRes.json(),
                agentsRes.json(),
            ]);
            setRoles(rolesData.roles || []);
            setAllChannels(channelsData.channels || []);
            setAllAgents(agentsData.agents || []);
        } catch (_) { /* ignore */ }
        setLoading(false);
    }, [authFetch]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const syncChannels = async () => {
        setSyncing(true);
        setSyncMsg(null);
        try {
            const res = await API('/channels/sync', { method: 'POST' }, authFetch);
            const data = await res.json();
            setSyncMsg(data.message || 'Sincronizado');
            await loadAll();
        } catch (e) {
            setSyncMsg('Error al sincronizar');
        }
        setSyncing(false);
        setTimeout(() => setSyncMsg(null), 4000);
    };

    const createRole = async (e) => {
        e.preventDefault();
        if (!newRoleName.trim()) return;
        setCreating(true);
        const res = await API('/roles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newRoleName.trim(), description: newRoleDesc.trim() })
        }, authFetch);
        if (res.ok) {
            setNewRoleName('');
            setNewRoleDesc('');
            await loadAll();
        }
        setCreating(false);
    };

    const handleDeleted = (roleId) => {
        setRoles(prev => prev.filter(r => r.id !== roleId));
    };

    return (
        <div className="space-y-8">
            {/* Page header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-100 tracking-tight">Roles & Canales</h1>
                <p className="text-slate-400 text-sm mt-1">
                    Gestiona los roles de agentes y los canales de WhatsApp asociados a cada uno.
                </p>
            </div>

            {/* Sync channels */}
            <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="font-semibold text-slate-200">Canales registrados</h2>
                    <button
                        onClick={syncChannels}
                        disabled={syncing}
                        className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg text-sm transition-colors disabled:opacity-60"
                    >
                        {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Sincronizar desde Hub
                    </button>
                </div>
                {syncMsg && (
                    <p className="text-xs text-primary mb-3">{syncMsg}</p>
                )}
                {loading ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Cargando…</div>
                ) : allChannels.length === 0 ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <AlertCircle size={14} />
                        Sin canales. Haz clic en "Sincronizar desde Hub" para importarlos.
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-3 mt-3">
                        {allChannels.map(ch => (
                            <div key={ch.id} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
                                <ChannelBadge 
                                    color={ch.color} 
                                    channel_name={ch.display_phone_number} 
                                    custom_name={ch.custom_name}
                                    size="md"
                                    showPhoneActions={false}
                                />
                                <div>
                                    {ch.display_phone_number ? (
                                        <CopyablePhone phone={ch.display_phone_number} textClassName="text-sm font-medium text-slate-200">
                                            {getChannelLabel(ch)}
                                        </CopyablePhone>
                                    ) : (
                                        <p className="text-sm font-medium text-slate-200">{getChannelLabel(ch) || 'Sin nombre'}</p>
                                    )}
                                    <p className="text-xs text-slate-500 font-mono">{ch.id}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Roles */}
            <section>
                <h2 className="font-semibold text-slate-200 mb-4">Roles</h2>

                {/* Create role form */}
                <form onSubmit={createRole} className="bg-slate-900/60 border border-dashed border-slate-700 rounded-xl p-4 mb-4 space-y-3">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nuevo rol</p>
                    <div className="flex gap-3">
                        <input
                            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary/60"
                            placeholder="Nombre del rol (ej. Ventas)"
                            value={newRoleName}
                            onChange={e => setNewRoleName(e.target.value)}
                            disabled={creating}
                            required
                        />
                        <input
                            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary/60"
                            placeholder="Descripción (opcional)"
                            value={newRoleDesc}
                            onChange={e => setNewRoleDesc(e.target.value)}
                            disabled={creating}
                        />
                        <button
                            type="submit"
                            disabled={creating || !newRoleName.trim()}
                            className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 flex items-center gap-2"
                        >
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Crear
                        </button>
                    </div>
                </form>

                {loading ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Cargando roles…</div>
                ) : roles.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No hay roles definidos todavía.</p>
                ) : (
                    <div className="space-y-3">
                        {roles.map(role => (
                            <RoleCard
                                key={role.id}
                                role={role}
                                allChannels={allChannels}
                                allAgents={allAgents}
                                authFetch={authFetch}
                                onDeleted={handleDeleted}
                                onUpdated={loadAll}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Unassigned conversations */}
            <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <h2 className="font-semibold text-slate-200 mb-1">Conversaciones sin canal</h2>
                <p className="text-xs text-slate-500 mb-4">
                    Conversaciones antiguas que no tienen canal asignado. Asígnalas manualmente.
                </p>
                <UnassignedConversations allChannels={allChannels} authFetch={authFetch} />
            </section>
        </div>
    );
}
